import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error | expired
  const [tx, setTx] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let stopped = false;
    let n = 0;

    const poll = async () => {
      if (stopped) return;
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/credits/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid" && data.credits_granted) {
          setStatus("paid");
          refresh?.();
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
        if (n >= MAX_ATTEMPTS) {
          setStatus("pending");
          if (intervalRef.current) clearInterval(intervalRef.current);
          return;
        }
      } catch (e) {
        setStatus("error");
        toast.error(e.response?.data?.detail || "Status check failed");
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    };

    poll();
    intervalRef.current = setInterval(poll, POLL_MS);
    return () => {
      stopped = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionId, refresh]);

  return (
    <div className="p-4 md:p-8 max-w-lg mx-auto" data-testid="payment-success-page">
      <div className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 md:p-6">
        {status === "checking" && (
          <div data-testid="payment-checking">
            <div className="text-[10px] font-mono-data text-[color:var(--text-muted)] tracking-widest">◆ CONFIRMING PAYMENT</div>
            <div className="font-serif-elite text-2xl text-white mt-2">Verifying with Stripe…</div>
            <div className="text-xs font-mono-data text-[color:var(--text-secondary)] mt-3">Attempt {attempts} / {MAX_ATTEMPTS}</div>
            <div className="mt-4 h-1 bg-[#27272A] overflow-hidden">
              <div className="h-full bg-gold animate-pulse" style={{ width: `${(attempts / MAX_ATTEMPTS) * 100}%` }} />
            </div>
          </div>
        )}

        {status === "paid" && tx && (
          <div data-testid="payment-paid">
            <div className="text-[10px] font-mono-data text-bull tracking-widest">◆ PAYMENT CONFIRMED</div>
            <div className="font-serif-elite text-2xl md:text-3xl text-white mt-2">You're topped up.</div>
            <div className="text-sm text-[color:var(--text-secondary)] mt-3">
              We credited your account with <span className="text-gold font-mono-data">{tx.credits}</span> credits from <span className="text-white">{tx.item_name}</span>.
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/credits" className="px-4 py-2 bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90 transition-colors" data-testid="back-credits-btn">
                VIEW BALANCE →
              </Link>
              <button onClick={() => navigate("/")} className="px-4 py-2 border border-[color:var(--border-default)] text-[11px] font-mono-data tracking-widest text-white hover:bg-[color:var(--bg-hover)] transition-colors" data-testid="back-home-btn">
                COMMAND CENTER
              </button>
            </div>
          </div>
        )}

        {status === "pending" && (
          <div data-testid="payment-pending">
            <div className="text-[10px] font-mono-data text-gold tracking-widest">◆ STILL PROCESSING</div>
            <div className="font-serif-elite text-2xl text-white mt-2">Taking a bit longer than usual.</div>
            <div className="text-sm text-[color:var(--text-secondary)] mt-3">
              Your bank may still be authorizing the charge. Credits will be granted automatically when Stripe confirms — usually within a minute. Refresh <Link to="/credits" className="text-gold underline">the credits page</Link> or check back shortly.
            </div>
          </div>
        )}

        {status === "expired" && (
          <div data-testid="payment-expired">
            <div className="text-[10px] font-mono-data text-bear tracking-widest">◆ SESSION EXPIRED</div>
            <div className="font-serif-elite text-2xl text-white mt-2">Payment session ended.</div>
            <div className="text-sm text-[color:var(--text-secondary)] mt-3">The Stripe checkout session expired before completion. Please try again.</div>
            <Link to="/credits" className="inline-block mt-4 px-4 py-2 bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90">
              ← BACK TO PLANS
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="payment-error">
            <div className="text-[10px] font-mono-data text-bear tracking-widest">◆ ERROR</div>
            <div className="font-serif-elite text-2xl text-white mt-2">Couldn't confirm payment.</div>
            <div className="text-sm text-[color:var(--text-secondary)] mt-3">
              If you were charged, please contact support with the session ID: <span className="font-mono-data text-white">{sessionId || "n/a"}</span>
            </div>
            <Link to="/credits" className="inline-block mt-4 px-4 py-2 bg-white text-black text-[11px] font-mono-data tracking-widest hover:opacity-90">
              ← BACK TO PLANS
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
